import { useEffect, useRef, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Building2, Upload, Image as ImageIcon, ExternalLink, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";
import SegmentMultiSelect from "../../components/SegmentMultiSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url ? (url.startsWith("http") ? url : `${BACKEND_URL}${url}`) : "";

const EMPTY_FORM = {
  banner_url: "",
  name: "",
  description: "",
  logo_url: "",
  url: "",
  allowed_segment_ids: [],
  is_active: true,
};

export default function AdminCobranding() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const bannerInput = useRef(null);
  const logoInput = useRef(null);

  const fetchAll = async () => {
    try {
      const r = await fetch(`${API}/admin/cobranding`, { credentials: "include" });
      if (r.ok) setItems(await r.json());
    } catch {
      toast.error("Failed to load cobranding entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      banner_url: c.banner_url || "",
      name: c.name || "",
      description: c.description || "",
      logo_url: c.logo_url || "",
      url: c.url || "",
      allowed_segment_ids: c.allowed_segment_ids || [],
      is_active: c.is_active !== false,
    });
    setOpen(true);
  };

  const upload = async (file, which) => {
    if (!file) return;
    const setBusy = which === "banner" ? setUploadingBanner : setUploadingLogo;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload/image`, { method: "POST", credentials: "include", body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const data = await r.json();
      setForm((p) => ({ ...p, [which === "banner" ? "banner_url" : "logo_url"]: data.url }));
      toast.success(`${which === "banner" ? "Banner" : "Logo"} uploaded`);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const url = editing ? `${API}/admin/cobranding/${editing.cobranding_id}` : `${API}/admin/cobranding`;
      const method = editing ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...form, name: form.name.trim() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success(editing ? "Cobranding updated" : "Cobranding created");
      setOpen(false);
      fetchAll();
    } catch (e2) {
      toast.error(e2.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      const r = await fetch(`${API}/admin/cobranding/${c.cobranding_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !c.is_active }),
      });
      if (!r.ok) throw new Error();
      fetchAll();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this cobranding entry?")) return;
    try {
      const r = await fetch(`${API}/admin/cobranding/${id}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error();
      toast.success("Deleted");
      fetchAll();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-cobranding">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cobranding</h1>
            <p className="text-sm text-gray-500 mt-0.5">Institutional banners shown on the customer home page based on segments.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700" onClick={openCreate} data-testid="add-cobranding-btn">
                <Plus size={18} className="mr-2" /> Add Cobranding
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit Cobranding" : "New Cobranding"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4">

                {/* Banner */}
                <div>
                  <Label>Banner Image</Label>
                  <input
                    ref={bannerInput}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => upload(e.target.files?.[0], "banner")}
                    data-testid="cobranding-banner-file"
                  />
                  {form.banner_url ? (
                    <div className="mt-1 relative rounded-xl overflow-hidden border border-gray-100">
                      <img src={resolveUrl(form.banner_url)} alt="Banner" className="w-full h-32 object-cover" />
                      <button type="button" onClick={() => bannerInput.current?.click()} className="absolute bottom-2 right-2 bg-white/90 text-gray-700 text-xs font-semibold rounded-full px-3 py-1.5 shadow">
                        Change
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => bannerInput.current?.click()} className="mt-1 w-full h-32 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:border-green-400 hover:text-green-600 flex flex-col items-center justify-center gap-1.5" data-testid="cobranding-banner-upload">
                      {uploadingBanner ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon size={20} />}
                      <span className="text-xs font-medium">{uploadingBanner ? "Uploading…" : "Upload banner"}</span>
                    </button>
                  )}
                </div>

                {/* Name */}
                <div>
                  <Label>Name of Institution</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. TCS, ICICI Bank" required data-testid="cobranding-name" />
                </div>

                {/* Description */}
                <div>
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Shown on the customer home card" rows={3} data-testid="cobranding-description" />
                </div>

                {/* Logo */}
                <div>
                  <Label>Institution Logo</Label>
                  <input
                    ref={logoInput}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => upload(e.target.files?.[0], "logo")}
                    data-testid="cobranding-logo-file"
                  />
                  {form.logo_url ? (
                    <div className="mt-1 flex items-center gap-3">
                      <div className="w-16 h-16 rounded-xl border border-gray-100 bg-gray-50 flex items-center justify-center overflow-hidden">
                        <img src={resolveUrl(form.logo_url)} alt="Logo" className="w-full h-full object-contain" />
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => logoInput.current?.click()}>Change</Button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => logoInput.current?.click()} className="mt-1 w-full h-20 border-2 border-dashed border-gray-200 rounded-xl text-gray-400 hover:border-green-400 hover:text-green-600 flex items-center justify-center gap-2" data-testid="cobranding-logo-upload">
                      {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={16} />}
                      <span className="text-xs font-medium">{uploadingLogo ? "Uploading…" : "Upload logo"}</span>
                    </button>
                  )}
                </div>

                {/* URL */}
                <div>
                  <Label>URL</Label>
                  <Input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="cobranding-url" />
                </div>

                {/* Segments */}
                <SegmentMultiSelect
                  value={form.allowed_segment_ids}
                  onChange={(ids) => setForm({ ...form, allowed_segment_ids: ids })}
                  label="Visible to Customer Segments"
                  helperText="Leave empty to show to all customers."
                  testIdPrefix="cobranding-form-segments"
                />

                {/* Active */}
                <div className="flex items-center justify-between pt-2">
                  <Label>Active</Label>
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="cobranding-active" />
                </div>

                <Button type="submit" disabled={saving} className="w-full bg-green-600 hover:bg-green-700" data-testid="cobranding-save">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : editing ? "Update" : "Create"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-2xl">
            <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700">No cobranding yet</p>
            <p className="text-xs text-gray-400 mt-1">Add your first institutional banner.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((c) => (
              <div key={c.cobranding_id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-sm transition-shadow" data-testid={`cobranding-row-${c.cobranding_id}`}>
                <div className="flex flex-col md:flex-row">
                  {c.banner_url && (
                    <div className="w-full md:w-48 h-32 md:h-auto flex-shrink-0 bg-gray-50">
                      <img src={resolveUrl(c.banner_url)} alt={c.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 p-4 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-shrink-0">
                      {c.logo_url ? (
                        <div className="w-12 h-12 rounded-xl border border-gray-100 bg-white overflow-hidden flex items-center justify-center">
                          <img src={resolveUrl(c.logo_url)} alt="" className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-gray-50 flex items-center justify-center text-gray-300">
                          <Building2 size={16} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-gray-900 truncate">{c.name}</h3>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                          {c.is_active ? "Active" : "Disabled"}
                        </span>
                      </div>
                      {c.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{c.description}</p>}
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-400">
                        {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-green-600">
                          <ExternalLink size={10} /> {c.url.replace(/^https?:\/\//, "").slice(0, 30)}
                        </a>}
                        <span>{c.allowed_segment_ids?.length || 0} segment(s)</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleActive(c)} className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-50" title={c.is_active ? "Disable" : "Enable"} data-testid={`toggle-cobranding-${c.cobranding_id}`}>
                        {c.is_active ? <ToggleRight size={18} className="text-green-500" /> : <ToggleLeft size={18} />}
                      </button>
                      <button onClick={() => openEdit(c)} className="p-2 text-gray-400 hover:text-green-600 rounded-lg hover:bg-gray-50" data-testid={`edit-cobranding-${c.cobranding_id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => remove(c.cobranding_id)} className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-50" data-testid={`delete-cobranding-${c.cobranding_id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
